import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useExternalControl, useSetDataAttrs } from 'hooks';
import { INumberPickerWidget } from 'types/widgets';
import styles from './number-picker.module.css';

interface IPropTypes {
  desc: INumberPickerWidget;
}

export const NumberPicker: React.FC<IPropTypes> = ({ desc }) => {
  const { id, initialValue, direction, formId, minValue, maxValue } = desc;

  const [value, setValue] = useState(initialValue);

  const onDecrement = useCallback((powerOf = 1) => {
    setValue((current) => {
      const stepLimit = Math.max(1, Math.floor((current - minValue) / 10));
      const step = Math.min(powerOf, stepLimit);
      const newValue = Math.max(minValue, current - step);

/*
      console.log(`decrement ${current} - ${Math.min(powerOf, stepLimit)} => ${newValue} /!* min(${powerOf}, ${stepLimit}) *!/`);
*/

      return newValue;
    });
  }, [minValue]);

  const onIncrement = useCallback((powerOf: number) => {
    setValue((current) => {
      const stepLimit = Math.max(1, Math.floor((maxValue - current) / 10));
      const step = Math.min(powerOf, stepLimit);
      const newValue = Math.min(maxValue, current + step);

/*
      console.log(`increment ${current} + ${Math.min(powerOf, stepLimit)} => ${newValue} /!* min(${powerOf}, ${stepLimit}) *!/`);
*/

      return newValue;
    });
  }, [maxValue]);

  return (
    <div id={id} className={styles.numberPicker} data-direction={direction}>
      {desc.children.map((child) => {
        switch (child._type) {
          case 'DECREMENT':
            return (
              <NumberPickerButton
                key={child.id}
                id={child.id}
                disabled={value <= minValue}
                onClick={onDecrement}
              >
                {icons.minus}
              </NumberPickerButton>
            );

          case 'INPUT':
            return (
              <NumberPickerValue
                key={child.id}
                name={id}
                form={formId}
                value={value}
                dataset={desc.dataset} /* It should inherit "root" widget dataset */
              />
            );

          case 'INCREMENT':
            return (
              <NumberPickerButton
                key={child.id}
                id={child.id}
                disabled={value >= maxValue}
                onClick={onIncrement}
              >
                {icons.plus}
              </NumberPickerButton>
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

const NumberPickerValue: React.FC<{
  name: string;
  value: number;
  form?: string;
  dataset: Array<[string, unknown]>;
}> = ({ name, form, value, dataset }) => {

  const ref=useRef<HTMLInputElement>(null);
  useSetDataAttrs(ref, dataset);
  return (
    <input
      ref={ref}
      name={name}
      form={form}
      value={value}
      className={styles.counter}
      data-role="number-picker-counter"
      tabIndex={-1}
      disabled
    />
  );
};

const NumberPickerButton: React.FC<{
  id: string;
  disabled: boolean;
  children: React.ReactNode;
  onClick: (_: number) => void;
}> = ({ id, disabled, children, onClick }) => {
  const ref = useExternalControl() as React.LegacyRef<HTMLButtonElement>;

  const timeout$ = React.useRef<ReturnType<typeof setTimeout>>();
  const interval$ = React.useRef<ReturnType<typeof setInterval>>();
  const pointerHandler = useCallback((ev: React.PointerEvent) => {
    if (ev.type === 'click') {
      onClick(1);
      return;
    }

    if (ev.type === 'pointerdown') {
      clearTimeout(timeout$.current);
      clearInterval(interval$.current);

      timeout$.current = setTimeout(() => {
        clearInterval(interval$.current);
        let cnt = 1;
        interval$.current = setInterval(() => {
          onClick(cnt++);
        }, 50);
      }, 500);
    } else {
      clearInterval(interval$.current);
      clearTimeout(timeout$.current);
    }
  }, [onClick]);

  useEffect(() => {
    const target = ref.current;

    const handler = (ev: Event) => {

      pointerHandler({type: ev.type.replace('proxy-', '')} as React.PointerEvent);
    }

    target?.addEventListener('proxy-pointerdown', handler);
    target?.addEventListener('proxy-pointerup', handler);
    target?.addEventListener('proxy-leave', handler);
    target?.addEventListener('proxy-pointercancel', handler);

    return () => {
      target?.removeEventListener('proxy-pointerdown', handler);
      target?.removeEventListener('proxy-pointerup', handler);
      target?.removeEventListener('proxy-leave', handler);
      target?.removeEventListener('proxy-pointercancel', handler);
    }
  }, [pointerHandler, ref]);
  return (
    <button id={id} ref={ref} data-disabled={disabled}
            onClick={pointerHandler as React.MouseEventHandler}
            onPointerDown={pointerHandler}
            onPointerUp={pointerHandler}
            onPointerCancel={pointerHandler}
            onPointerLeave={pointerHandler}
    >
      {children}
    </button>
  );
};

const icons = {
  plus: (
    <svg width="24" height="24" viewBox="0 0 24 24">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M11 20.3C11 20.6866 11.3134 21 11.7 21H12.3C12.6866 21 13 20.6866 13 20.3V13L20.3 13C20.6866 13 21 12.6866 21 12.3V11.7C21 11.3134 20.6866 11 20.3 11L13 11V3.7C13 3.3134 12.6866 3 12.3 3H11.7C11.3134 3 11 3.3134 11 3.7V11L3.7 11C3.3134 11 3 11.3134 3 11.7L3 12.3C3 12.6866 3.3134 13 3.7 13L11 13V20.3Z"
      ></path>
    </svg>
  ),
  minus: (
    <svg width="24" height="24" viewBox="0 0 24 24">
      <rect
        x="21"
        y="13"
        width="18"
        height="2"
        rx="0.7"
        transform="rotate(180 21 13)"
      />
    </svg>
  ),
};
